/**
 * Array Utilities
 * Memory-optimized array operations with object pooling
 */

import { GALLERY_CONFIG } from '../gallery-config.js';
import { arrayPool } from './pool-utils.js';

export const ArrayUtils = {

  /**
   * Shuffle array in-place using Fisher-Yates algorithm
   * WARNING: Modifies the original array!
   * @param {Array} array - Array to shuffle (will be modified)
   * @returns {Array} The same array reference, shuffled
   */
  shuffleInPlace(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  },

  /**
   * @param {number} size - Minimum size needed
   * @returns {Array} Reusable array from pool
   */
  getWorkingArray(size = 250) {
    return arrayPool.getArray(size);
  },

  /**
   * @param {Array} array - Array to return to pool
   */
  releaseArray(array) {
    arrayPool.releaseArray(array);
  },

  /**
   * @param {Array} source - Source array to copy
   * @returns {Array} New array with copied elements
   */
  copyArrayOptimized(source) {
    const workingArray = this.getWorkingArray(source.length);

    for (let i = 0; i < source.length; i++) {
      workingArray[i] = source[i];
    }
    workingArray.length = source.length;

    return workingArray;
  },

  /**
   * @returns {Object} Pool performance statistics
   */
  getPoolStats() {
    return arrayPool.getStats();
  },

  clearPool() {
    arrayPool.clear();
  },

  /**
   * Memory-optimized randomization for gallery artworks
   * Uses object pooling to reduce garbage collection
   * @param {Array} source - Source array of artworks
   * @param {number} count - Number of items to select
   * @returns {Array} Randomly selected items
   */
  randomizeSelection(source, count) {
    if (source.length < count) {
      throw new Error(`Not enough items: need ${count}, have ${source.length}`);
    }

    const workingArray = this.copyArrayOptimized(source);
    this.shuffleInPlace(workingArray);
    const result = workingArray.slice(0, count);
    this.releaseArray(workingArray);

    return result;
  },

  /**
   * Randomly select one item from filtered array
   * Used for selecting third artwork when expanding to trio
   * @param {Array} source - Source array to select from
   * @param {Array} excludeIds - IDs to exclude from selection
   * @param {number} minIndex - Minimum index to start from (defaults to MIN_CAROUSEL_INDEX)
   * @returns {Object|null} Randomly selected item or null if none available
   */
  randomSelectFromFiltered(source, excludeIds = [], minIndex = GALLERY_CONFIG.LAYOUT.MIN_CAROUSEL_INDEX) {
    const available = source
      .slice(minIndex)
      .filter(item => !excludeIds.includes(item.id));

    if (available.length === 0) return null;

    const randomIndex = Math.floor(Math.random() * available.length);
    return available[randomIndex];
  }
};